import {STUDENT_PATH,STUDENT_TITLE,STUDENT_H1,STUDENT_DESCRIPTION,STUDENT_FAQ} from "../content/studentGuide.js";
import {SELLING_PATH,SELLING_TITLE,SELLING_H1,SELLING_DESCRIPTION,SELLING_FAQ} from "../content/sellingGuide.js";
import { GUIDE_PATH,GUIDE_TITLE,GUIDE_H1,GUIDE_DESCRIPTION,GUIDE_FAQ } from "../content/classTripGuide.js";
import {CLASS_FAQ_ITEMS} from "../content/classLanding.js";
import {getClassLandingMeta} from "./classLandingMeta.js";
import { FAQ_ITEMS } from "./faqContent.js";
import { TEAM_FAQ_ITEMS } from "../content/teamFaq.js";

export const SITE_URL = "https://www.salespartner.se";
export const SITE_NAME = "SalesPartner";
export const DEFAULT_IMAGE_PATH = "/salespartner-og.png";
export const DEFAULT_IMAGE = `${SITE_URL}${DEFAULT_IMAGE_PATH}`;

export const SEO_LINKS = [
  { to: "/sa-fungerar-salespartner", label: "Se hur det fungerar" },
  { to: "/provision-och-fortjanst", label: "Provision och förtjänst" },
  { to: "/vanliga-fragor", label: "Vanliga frågor" },
  { to: "/tjana-pengar-till-laget", label: "Tjäna pengar till laget" },
  { to: "/tjana-pengar-till-foreningen", label: "Tjäna pengar till föreningen" },
  { to: "/klassforsaljning", label: "Klassförsäljning" },
];

export const ROUTE_META = {
  "/": {
    title: "Gruppförsäljning för klass, lag och förening | SalesPartner",
    description:
      "SalesPartner hjälper skolklasser, idrottslag och föreningar att samla in pengar med digital gruppförsäljning. Välj rätt upplägg och läs hur det fungerar.",
    h1: "Samla in pengar till klassen, laget eller föreningen",
    lead:
      "SalesPartner är en digital gruppförsäljning med personliga säljlänkar. Välj upplägg för klass, lag eller förening och gå vidare till rätt sida.",
    sections: [
      {
        heading: "Välj rätt upplägg",
        text: "Skolklasser, idrottslag och föreningar använder SalesPartner på olika sätt. Välj klassförsäljning, lagförsäljning eller föreningsförsäljning för mer detaljer.",
      },
      {
        heading: "Så fungerar SalesPartner",
        text: "Gruppledaren registrerar gruppen, bjuder in säljarna och delar personliga länkar och QR-koder. Kunden beställer, leveransen går till kunden eller gruppen, och förtjänsten betalas ut efter avslut.",
      },
      {
        heading: "Räkna på förtjänsten",
        text: "Provisionen är produkt- och variantbunden. En kalkylator hjälper gruppen att planera. Utförligare svar finns på sidorna för provision och vanliga frågor.",
      },
    ],
    schema: ["organization", "website"],
  },
  "/sa-fungerar-salespartner": {
    title: "Så fungerar SalesPartner | Gruppförsäljning steg för steg",
    description:
      "Så fungerar SalesPartner från registrering och säljlänkar till beställning, leverans och utbetalning av gruppens förtjänst.",
    h1: "Så fungerar SalesPartner – steg för steg",
    lead:
      "SalesPartner samlar gruppens försäljning i ett digitalt flöde från registrering till leverans och utbetalning.",
    sections: [
      {
        heading: "Från registrering till utbetalning",
        text: "En vuxen gruppledare registrerar gruppen. Efter godkännande bjuds säljarna in, kunden beställer via personlig länk och förtjänsten kan betalas ut efter leverans och väntetid.",
      },
      {
        heading: "Så fungerar det för säljaren och kunden",
        text: "Säljaren delar en personlig länk och QR-kod och behöver inte hantera kontanter. Kunden väljer ur aktuellt sortiment och ser de betalningsalternativ som finns i kassan.",
      },
      {
        heading: "Välj rätt upplägg för er grupp",
        text: "Processen är densamma. Klass, lag och förening har egna sidor med mer om respektive upplägg.",
      },
    ],
  },
  "/provision-och-fortjanst": {
    title: "Provision och förtjänst för gruppförsäljning",
    description:
      "Se hur SalesPartners produktbundna provision fungerar, räkna på ett möjligt utfall och läs när gruppens förtjänst kan betalas ut.",
    h1: "Räkna på gruppens förtjänst",
    lead:
      "Prova olika mål och gruppstorlekar. Kalkylatorn använder 74 kr i genomsnittlig provision per såld produkt som planeringsvärde.",
    sections: [
      {
        heading: "Provisionen följer produkten",
        text: "Förtjänsten beräknas utifrån antal sålda produkter och den provision som gäller för respektive produkt eller variant.",
      },
      {
        heading: "Räkna med ett tydligt genomsnitt",
        text: "Dra i reglagen för gruppens mål och antal säljare. Kalkylatorn visar ungefär hur många produkter varje säljare behöver sälja och använder 74 kr i genomsnittlig provision per produkt.",
      },
      {
        heading: "Utbetalning efter leverans",
        text: "När alla beställningar har levererats och väntetiden har passerat kan gruppen begära utbetalning. Returer och återbetalningar kan påverka beloppet.",
      },
    ],
  },
  "/vanliga-fragor": {
    title: "Vanliga frågor om gruppförsäljning | SalesPartner",
    description:
      "Svar om hur ni registrerar en grupp, säljer med personliga länkar, hur betalning och leverans fungerar och hur gruppens förtjänst räknas.",
    h1: "Vanliga frågor om SalesPartner",
    lead:
      "Korta svar om registrering, försäljning, betalning, leverans och förtjänst. Fördjupning finns på de sidor som äger respektive fråga.",
    sections: FAQ_ITEMS.map(({ q, a }) => ({ heading: q, text: a })),
    schema: ["faq"],
  },
  "/tjana-pengar-till-laget": {
    title: "Tjäna pengar till laget | SalesPartner",
    description:
      "Samla in pengar till laget med personliga säljlänkar, tydligt gruppansvar och leverans som passar lagets upplägg. Läs hur det fungerar.",
    h1: "Tjäna pengar till lagets cup, läger eller utrustning",
    lead:
      "Lagledaren registrerar laget. Spelarna delar personliga säljlänkar och QR-koder. Laget säljer produkter ur SalesPartners aktuella sortiment och väljer leverans som passar cup, läger, utrustning eller lagkassa.",
    sections: [
      {
        heading: "Så fungerar lagförsäljning med SalesPartner",
        text: "Lagledaren registrerar laget och lämnar uppgifter om den planerade försäljningsperioden. Registreringen är en ansökan och måste godkännas innan laget går vidare. Efter godkännande slutför lagledaren onboarding och bjuder in säljarna. Varje säljare får en personlig säljlänk och QR-kod. Beställningar via säljlänken kopplas till rätt säljare och rätt lag. Kunden beställer via säljlänken. De betalningsalternativ som är tillgängliga visas i kassan. Direktbetalning hanteras via Stripe. Gruppfaktura kan användas tillsammans med gemensam leverans när det alternativet finns för beställningen. Säljarna behöver inte hantera kontanter. Beställningen levereras antingen direkt till kunden eller gemensamt till laget.",
      },
      {
        heading: "Vad laget kan samla pengar till",
        text: "Samma upplägg kan användas till cup, träningsläger, utrustning eller en gemensam lagkassa. Målet sätts av laget.",
      },
      {
        heading: "Leveransalternativ",
        text: "Vid direktleverans skickas produkterna till kunden. Normal leveranstid är 3–5 arbetsdagar efter mottagen betalning och bekräftad order. Vid gruppleverans skickas beställningarna till lagets adress och lagledaren ansvarar för utdelningen. Gruppleverans kan variera beroende på försäljningsperiod och lagets planering. Aktuella fraktvillkor finns på sidan för leveransvillkor.",
      },
      {
        heading: "Hur förtjänsten räknas och betalas ut",
        text: "Provisionen är produkt- och variantbunden och kan variera. Lagets verkliga förtjänst räknas på den faktiska provisionen för det som säljs. För planering finns en kalkylator som använder 74 kr i genomsnittlig provision per såld produkt som planeringsvärde. Det är inte en garanti eller fast provision. Efter att alla beställningar har levererats och väntetiden på 14 dagar har passerat kan laget begära utbetalning. Returer och återbetalningar kan påverka slutbeloppet.",
      },
      {
        heading: "Vad lagledaren ansvarar för",
        text: "Lagledaren sköter registreringen och onboarding, bjuder in säljarna och håller ihop lagets upplägg. Vid gruppleverans tar lagledaren emot leveransen och ansvarar för utdelningen.",
      },
      {
        heading: "Tjäna pengar till cup och träningsläger",
        text: "En cup eller ett träningsläger är ett konkret mål som laget kan planera försäljningen kring. Samma flöde gäller: lagledaren registrerar laget, spelarna delar sina länkar och laget väljer det leveransupplägg som går att hantera.",
      },
      {
        heading: "Bygg upp lagkassan",
        text: "Samma försäljningsupplägg kan användas för ett gemensamt lagmål. Förtjänsten följer den aktuella kampanjen och betalas ut till gruppen enligt utbetalningsvillkoren.",
      },
      {
        heading: "Kom igång",
        text: "Registrera laget via det publika formuläret. Läs processidan för hela flödet och vanliga frågor om registrering, leverans och utbetalning.",
      },
    ],
  },
  "/tjana-pengar-till-foreningen": {
    title: "Tjäna pengar till föreningen | SalesPartner",
    description:
      "Samla in pengar till föreningen genom lag och grupper som driver tydliga digitala försäljningar. Läs hur upplägget fungerar med SalesPartner.",
    h1: "Tjäna pengar till föreningen med tydliga grupper",
    lead:
      "Föreningens lag och grupper kan samla in pengar med egna gruppledare, personliga säljlänkar och ett digitalt flöde från beställning till utbetalning.",
    sections: [
      {
        heading: "Så fungerar föreningsförsäljning med SalesPartner",
        text: "Föreningens lag eller grupper kan använda SalesPartner för att samla in pengar. Varje grupp behöver en tydlig gruppledare som håller ihop försäljningen. Säljarna får personliga säljlänkar och QR-koder. Beställningar via länken kopplas till rätt säljare och rätt grupp.",
      },
      {
        heading: "Ett upplägg för föreningens lag och grupper",
        text: "Ett enskilt lag kan registreras via det publika formuläret, som stöder idrottslag och skolklass. En större samordning av flera lag är ett nästa steg, inte ett krav för att komma igång.",
      },
      {
        heading: "Så går försäljningen till",
        text: "Gruppledaren registrerar gruppen och lämnar uppgifter om den planerade försäljningsperioden. Registreringen är en ansökan och måste godkännas innan gruppen går vidare. Efter godkännande slutför gruppledaren onboarding och bjuder in säljarna. Varje säljare får en personlig säljlänk och QR-kod. Kunden beställer via säljlänken. De betalningsalternativ som är tillgängliga visas i kassan. Beställningen levereras antingen direkt till kunden eller gemensamt till gruppen. När försäljningsperioden är slut avslutas kampanjen. Efter leverans och väntetiden kan gruppens förtjänst betalas ut.",
      },
      {
        heading: "Hur förtjänsten hanteras",
        text: "Provisionen är produkt- och variantbunden och kan variera. Gruppens verkliga förtjänst räknas på den faktiska provisionen för det som säljs. För planering finns en kalkylator som använder 74 kr i genomsnittlig provision per såld produkt som planeringsvärde. Det är inte en garanti eller fast provision. Efter att alla beställningar har levererats och väntetiden på 14 dagar har passerat kan gruppen begära utbetalning. Returer och återbetalningar kan påverka slutbeloppet.",
      },
      {
        heading: "Vad gruppledaren ansvarar för",
        text: "Gruppledaren sköter registreringen och onboarding, bjuder in säljarna och håller ihop gruppen. Vid gruppleverans tar gruppledaren emot leveransen och ansvarar för utdelningen.",
      },
      {
        heading: "När flera lag ska delta",
        text: "Ett lag eller en grupp kan köra sitt eget upplägg. Ska flera lag samordnas hjälper SalesPartner till att hitta rätt struktur innan start.",
      },
      {
        heading: "Kom igång",
        text: "Registrera ett lag eller en grupp via det publika formuläret, eller hör av er om flera lag ska samordnas. Läs processidan för hela flödet och vanliga frågor om registrering, leverans och utbetalning.",
      },
    ],
  },
  "/klassforsaljning": {
    ...getClassLandingMeta(typeof __CLASS_MAX_COMMISSION__ === "undefined" ? null : __CLASS_MAX_COMMISSION__),
  },
  "/products": {
    title: "Aktuella produkter i SalesPartner",
    description:
      "Se de produkter och varianter som är aktiva i SalesPartners shop. Sortiment, pris och produktbunden provision kan förändras.",
    h1: "Lättsålda produkter alla vill ha",
    lead: "Här visas de produkter och varianter som för närvarande är aktiva i SalesPartners shop.",
    sections: [{ heading: "Så fungerar produkterna i gruppförsäljningen", text: "Med SalesPartner säljer klassen, laget eller föreningen produkter ur det aktuella sortimentet. Här kan ni se vilka produkter och varianter som finns just nu, jämföra priser och läsa produktinformationen. Sortimentet kan förändras. Kontrollera därför alltid den aktuella produktsidan inför ett köp, särskilt om en produkt finns i flera storlekar, färger eller utföranden. Gruppens förtjänst bygger på provisionen för varje såld produkt eller variant. Beloppet kan skilja sig mellan produkter och varianter; det är inte en gemensam procentsats för hela sortimentet. Antalet sålda produkter och eventuella returer påverkar den slutliga förtjänsten. Läs mer om provision och förtjänst för att förstå beräkningen och planera gruppens försäljning. Säljarna delar sina personliga säljlänkar så att beställningarna kopplas till rätt grupp och säljare. Kunden väljer produkter och lägger sin beställning i shopen. Tillgängliga betalningsalternativ och den slutliga fraktkostnaden visas i kassan innan beställningen slutförs. Leveransen kan gå hem till kunden eller, när gruppen erbjuder det, samlat till gruppen för utdelning efter säljperioden. Vilka val som är möjliga framgår i den aktuella shopen och kassan. Gruppledaren håller ihop försäljningen och ansvarar för utdelningen vid gruppleverans. På sidan så fungerar SalesPartner kan ni följa hela processen från registrering och säljlänkar till leverans och utbetalning." }],
  },
  "/contact": {
    title: "Kontakta SalesPartner",
    description:
      "Kontakta SalesPartner om gruppregistrering, större föreningsupplägg, beställningar eller support via mejl, telefon eller kontaktformulär.",
    h1: "Vi hjälper er välja rätt upplägg",
    lead: "Vi hjälper till med frågor om gruppupplägg, försäljning, beställningar och support.",
    sections: [
      { heading: "Kontaktuppgifter och svarstid", text: "SalesPartner drivs av EMW Trading AB, org.nr 5594624669. Skogssvedjegatan 35 b, 506 49 Borås. Mejla info@salespartner.se eller ring 010-182 50 00. Vi återkommer inom ett dygn." },
      { heading: "Hur kommer vår grupp igång?", text: "En vuxen gruppledare registrerar gruppen. Registreringen granskas innan ni går vidare med försäljningen. Läs vanliga frågor om registrering." },
      { heading: "Vad behöver ni veta om min beställning?", text: "Beskriv vad du behöver hjälp med och ange gärna ordernumret i ditt mejl. Skicka aldrig kortuppgifter eller lösenord. På sidan vanliga frågor finns också svar om betalning och leverans." },
      { heading: "Hur räknas gruppens förtjänst?", text: "Förtjänsten följer provisionen för de produkter och varianter som säljs. Returer kan påverka slutbeloppet. Läs vanliga frågor om provision och utbetalning eller kontakta oss om du behöver hjälp med gruppens underlag." },
    ],
  },
  "/kopvillkor": {
    title: "Köpvillkor | SalesPartner",
    description: "Läs SalesPartners villkor för beställning, betalning, leverans, reklamation och retur.",
    h1: "Köpvillkor",
    lead: "Villkor för köp genom SalesPartner.",
    sections: [],
  },
  "/integritetspolicy": {
    title: "Integritetspolicy | SalesPartner",
    description: "Läs hur SalesPartner behandlar personuppgifter och vilka rättigheter du har.",
    h1: "Integritetspolicy",
    lead: "Information om SalesPartners behandling av personuppgifter.",
    sections: [],
  },
  "/cookiepolicy": {
    title: "Cookiepolicy | SalesPartner",
    description: "Läs hur SalesPartner använder cookies och lokal lagring, vilka val du har och hur du ändrar dina cookieinställningar.",
    h1: "Cookiepolicy",
    lead: "Information om cookies och val på SalesPartners webbplats.",
    sections: [],
  },
  "/retur-och-angerratt": {
    title: "Retur och ångerrätt | SalesPartner",
    description: "Läs SalesPartners villkor för retur, ångerrätt, återbetalning och reklamation.",
    h1: "Retur och ångerrätt",
    lead: "Villkor för retur och ångerrätt.",
    sections: [],
  },
  "/leveransvillkor": {
    title: "Leveransvillkor | SalesPartner",
    description: "Läs om direktleverans, gruppleverans, fraktavgifter och leveranstider hos SalesPartner.",
    h1: "Leveransvillkor",
    lead: "Villkor för SalesPartners leveransalternativ.",
    sections: [],
  },
  "/gruppledaravtal": {
    title: "Gruppledaravtal | SalesPartner",
    description: "Läs gruppledarens ansvar och villkor för att driva en gruppförsäljning med SalesPartner.",
    h1: "Gruppledaravtal",
    lead: "Villkor och ansvar för gruppledare.",
    sections: [],
  },
  "/utbetalningsvillkor": {
    title: "Utbetalningsvillkor | SalesPartner",
    description: "Läs när och hur gruppens provision kan betalas ut och hur returer påverkar beloppet.",
    h1: "Utbetalningsvillkor",
    lead: "Villkor för utbetalning av gruppens provision.",
    sections: [],
  },
  [GUIDE_PATH]:{title:GUIDE_TITLE,h1:GUIDE_H1,description:GUIDE_DESCRIPTION},
  [SELLING_PATH]:{title:SELLING_TITLE,h1:SELLING_H1,description:SELLING_DESCRIPTION},
  [STUDENT_PATH]:{title:STUDENT_TITLE,h1:STUDENT_H1,description:STUDENT_DESCRIPTION},
};

export const NOINDEX_META = {
  title: "SalesPartner",
  description: "SalesPartners digitala tjänst för gruppförsäljning.",
  robots: "noindex, follow",
  h1: "SalesPartner",
  lead: "",
  sections: [],
};

// Application pages remain noindex and do not gain public canonicals or sitemap entries.
export const PRIVATE_ROUTE_META = {
  "/checkout": {
    title: "Kassan | SalesPartner",
    description: "Slutför din beställning hos SalesPartner. Säker betalning och leverans hem eller till gruppen – stöd klassen, laget eller föreningen.",
  },
  "/login": {
    title: "Logga in | SalesPartner",
    description: "Logga in på SalesPartner för att följa försäljningen, dela säljlänkar och se gruppens förtjänst i realtid.",
  },
  "/register-group": {
    title: "Registrera din grupp | SalesPartner",
    description: "Registrera din klass, ditt lag eller din förening hos SalesPartner och kom igång med digital gruppförsäljning på några minuter.",
  },
};

export const NOT_FOUND_META = {
  title: "Sidan hittades inte | SalesPartner",
  description: "Sidan du söker finns inte. Gå till startsidan eller se hur SalesPartner fungerar.",
  robots: "noindex, follow",
  h1: "Sidan hittades inte",
  lead: "Länken kan vara inaktuell eller adressen felstavad.",
  sections: [],
};

export const NOINDEX_PREFIXES = ["/admin", "/group-admin", "/seller", "/association"];
export const NOINDEX_EXACT = [
  "/shop",
  "/login",
  "/checkout",
  "/success",
  "/register-group",
  "/auth/reset-password",
  "/fortnox/callback",
];
export const NOINDEX_DYNAMIC = ["/register-seller/", "/seller/public/", "/group/", "/shop/products/"];

export const PRERENDER_ROUTES = Object.keys(ROUTE_META);
export const SITEMAP_ROUTES = [
  "/",
  "/sa-fungerar-salespartner",
  "/provision-och-fortjanst",
  "/vanliga-fragor",
  "/tjana-pengar-till-laget",
  "/tjana-pengar-till-foreningen",
  "/klassforsaljning",
  "/products",
  "/contact",
  "/kopvillkor",
  "/integritetspolicy",
  "/cookiepolicy",
  "/retur-och-angerratt",
  "/leveransvillkor",
  "/gruppledaravtal",
  "/utbetalningsvillkor",
  GUIDE_PATH,
  SELLING_PATH,
  STUDENT_PATH,
];

export const LEGACY_REDIRECTS = {
  "/selling": "/sa-fungerar-salespartner",
  "/earn-money": "/provision-och-fortjanst",
  "/faq": "/vanliga-fragor",
  "/marketing": "/",
};

export function normalizePathname(pathname) {
  const value = typeof pathname === "string" && pathname ? pathname : "/";
  if (value === "/") return "/";
  return value.replace(/\/+$/, "") || "/";
}

export function resolveMetadataPathname(pathname) {
  const normalizedPathname = normalizePathname(pathname);
  return LEGACY_REDIRECTS[normalizedPathname] || normalizedPathname;
}

export function isNoindexRoute(pathname) {
  const normalizedPathname = normalizePathname(pathname);
  if (NOINDEX_EXACT.includes(normalizedPathname)) return true;
  if (NOINDEX_PREFIXES.some((prefix) => normalizedPathname.startsWith(prefix))) return true;
  return NOINDEX_DYNAMIC.some((prefix) => normalizedPathname.startsWith(prefix));
}

export function getRouteMeta(pathname) {
  const normalizedPathname = resolveMetadataPathname(pathname);
  if (ROUTE_META[normalizedPathname]) {
    return { ...ROUTE_META[normalizedPathname], robots: "index, follow" };
  }
  if (isNoindexRoute(normalizedPathname)) {
    return PRIVATE_ROUTE_META[normalizedPathname]
      ? { ...NOINDEX_META, ...PRIVATE_ROUTE_META[normalizedPathname] }
      : NOINDEX_META;
  }
  return NOT_FOUND_META;
}

export function getCanonicalUrl(pathname) {
  const normalizedPathname = resolveMetadataPathname(pathname);
  if (!ROUTE_META[normalizedPathname]) return null;
  return `${SITE_URL}${normalizedPathname === "/" ? "/" : normalizedPathname}`;
}

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "SalesPartner",
  legalName: "EMW Trading AB",
  url: SITE_URL,
  logo: `${SITE_URL}/SalesPartner-03-clean.svg`,
  email: "info@salespartner.se",
  telephone: "+46101825000",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Skogssvedjegatan 35 b",
    postalCode: "506 49",
    addressLocality: "Borås",
    addressCountry: "SE",
  },
};

const website = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  inLanguage: "sv-SE",
  publisher: {
    "@type": "Organization",
    name: "SalesPartner",
  },
};

const faqPage = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: {
      "@type": "Answer",
      text: a,
    },
  })),
};

export function getStructuredData(pathname, buildDate = typeof __GUIDE_BUILD_DATE__ === "undefined" ? new Date().toISOString() : __GUIDE_BUILD_DATE__) {
  const normalizedPathname = resolveMetadataPathname(pathname);
  if (normalizedPathname === '/klassforsaljning') return [{
    '@context':'https://schema.org', '@type':'FAQPage',
    mainEntity:CLASS_FAQ_ITEMS.map(({q,a})=>({'@type':'Question',name:q,acceptedAnswer:{'@type':'Answer',text:a}})),
  }];
  if (normalizedPathname === '/tjana-pengar-till-laget') return [{
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: TEAM_FAQ_ITEMS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  }];
  if(normalizedPathname === STUDENT_PATH) return [
    {'@context':'https://schema.org','@type':'Article',headline:STUDENT_H1,author:{'@type':'Organization',name:'SalesPartner'},publisher:{'@type':'Organization',name:'SalesPartner'},datePublished:buildDate,dateModified:buildDate,mainEntityOfPage:SITE_URL+STUDENT_PATH},
    {'@context':'https://schema.org','@type':'BreadcrumbList',itemListElement:[['Start','/'],['Klassförsäljning','/klassforsaljning'],['Tjäna pengar till studenten',STUDENT_PATH]].map(([name,path],i)=>({'@type':'ListItem',position:i+1,name,item:SITE_URL+path}))},
    {'@context':'https://schema.org','@type':'FAQPage',mainEntity:STUDENT_FAQ.map(({q,a})=>({'@type':'Question',name:q,acceptedAnswer:{'@type':'Answer',text:a}}))},
  ];
  if(normalizedPathname === SELLING_PATH) return [
    {"@context":"https://schema.org","@type":"Article",headline:SELLING_H1,author:{"@type":"Organization",name:"SalesPartner"},publisher:{"@type":"Organization",name:"SalesPartner"},datePublished:buildDate,dateModified:buildDate,mainEntityOfPage:SITE_URL+SELLING_PATH},
    {"@context":"https://schema.org","@type":"BreadcrumbList",itemListElement:[["Start","/"],["Klassförsäljning","/klassforsaljning"],["Sälja med klassen",SELLING_PATH]].map(([name,path],i)=>({"@type":"ListItem",position:i+1,name,item:SITE_URL+path}))},
    {"@context":"https://schema.org","@type":"FAQPage",mainEntity:SELLING_FAQ.map(({q,a})=>({"@type":"Question",name:q,acceptedAnswer:{"@type":"Answer",text:a}}))},
  ];
  if(normalizedPathname === GUIDE_PATH) return [
    {'@context':'https://schema.org','@type':'Article',headline:GUIDE_H1,author:{'@type':'Organization',name:'SalesPartner'},publisher:{'@type':'Organization',name:'SalesPartner'},datePublished:buildDate,dateModified:buildDate,mainEntityOfPage:SITE_URL+GUIDE_PATH},
    {'@context':'https://schema.org','@type':'BreadcrumbList',itemListElement:[['Start','/'],['Klassförsäljning','/klassforsaljning'],['Tjäna pengar till klassresan',GUIDE_PATH]].map(([name,path],i)=>({'@type':'ListItem',position:i+1,name,item:SITE_URL+path}))},
    {'@context':'https://schema.org','@type':'FAQPage',mainEntity:GUIDE_FAQ.map(({q,a})=>({'@type':'Question',name:q,acceptedAnswer:{'@type':'Answer',text:a}}))},
  ];
  const schema = ROUTE_META[normalizedPathname]?.schema || [];
  return schema.flatMap((type) => {
    if (type === "organization") return [organization];
    if (type === "website") return [website];
    if (type === "faq") return [faqPage];
    return [];
  });
}
